/**
 * ThreadHistoryList — the shared "past conversations" primitive (#931).
 *
 * Derived from ConversationRail's thread rows (list + resume; the rail has no
 * search, so neither does this). The rail stays the full-featured /chat
 * sidebar — rename, delete, new-thread live there with their backing routes.
 * This primitive is the portable subset every other surface needs: a list of
 * threads and a tap to resume one. It ships in the widget header for both
 * personas (via ChatPanelShell's `headerExtras` slot) and in the Williams
 * page drawer's recents.
 *
 * `ThreadHistoryControl` is the widget-header packaging: a History button that
 * opens a popover, fetches through the injected `getConversations` on each
 * open (so a freshly-retired thread shows up), and resumes through the
 * injected `onResume` — the Williams branch routes that through
 * `williamsApi.createSession` BEFORE the SSE connect per the §6 backend
 * contract; the primitive itself stays channel-agnostic.
 */
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { History, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COARSE_HIT_28 } from '@/lib/touch-target';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MonoLabel } from '@/components/editorial';
import { deriveConversationDisplayState } from '@/lib/chat-display';
import { formatNumber, formatTimeAgo } from '@/i18n/intl';
import { useMsg } from '@/i18n/msg';
import { useUiLanguage } from '@/features/chat-trf/language';
import type { ConversationSummary } from '@/types/chat';

// ─── List primitive ────────────────────────────────────────────────

interface ThreadHistoryListProps {
  conversations: ConversationSummary[];
  /** The thread the surface is currently attached to — marked, not resumable. */
  activeId?: string;
  onResume: (id: string) => void;
  emptyLabel?: string;
  /**
   * The list has not settled yet. A value the surface could not read is never
   * invented (§1.9): the empty label waits until the fetch has answered.
   */
  loading?: boolean;
  className?: string;
}

export function ThreadHistoryList({
  conversations,
  activeId,
  onResume,
  emptyLabel,
  loading = false,
  className,
}: ThreadHistoryListProps) {
  const { t } = useTranslation('chat');
  const resolve = useMsg();
  const language = useUiLanguage();
  // One Intl formatter serves every language, English included (#3168);
  // an unparseable stamp returns '' rather than throwing.
  const relativeOf = (iso: string | undefined): string => formatTimeAgo(iso, { locale: language });
  if (loading) {
    return (
      <div className={cn('flex items-center justify-center gap-2 px-4 py-6 text-stevens-ink-3', className)}>
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-body">{t('history.loading')}</span>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className={cn('px-4 py-6 text-center', className)}>
        <p className="font-serif text-body text-stevens-ink-3 italic">{emptyLabel ?? t('history.empty')}</p>
      </div>
    );
  }

  return (
    <div className={cn('py-1', className)}>
      {conversations.map((conv) => {
        const isActive = conv.id === activeId;
        const { lastActiveLabel, isDormant } = deriveConversationDisplayState(conv.updated_at);
        const relativeLabel = relativeOf(conv.updated_at) || resolve(lastActiveLabel);

        return (
          <button
            key={conv.id}
            type="button"
            onClick={() => onResume(conv.id)}
            aria-current={isActive || undefined}
            className={cn(
              'relative flex w-full items-start px-4 py-2.5 text-left transition-colors',
              isActive ? 'bg-stevens-indigo/5' : 'hover:bg-muted/60',
              isDormant && !isActive && 'opacity-70',
            )}
          >
            <span
              className={cn(
                'absolute left-0 top-0 h-full w-[2px]',
                isActive ? 'bg-stevens-indigo' : isDormant ? 'bg-stevens-rule-2' : 'bg-transparent',
              )}
              aria-hidden
            />
            <span className="flex-1 min-w-0">
              <span
                className={cn(
                  'block text-body leading-tight truncate text-foreground',
                  isActive && 'font-medium',
                )}
              >
                {conv.title?.trim() || t('history.untitled')}
              </span>
              <MonoLabel className="block mt-0.5">
                {relativeLabel}
                {isDormant && <>{' · '}{t('history.dormant')}</>}
                {isActive && <>{' · '}{t('history.current')}</>}
              </MonoLabel>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Widget-header control (headerExtras packaging) ────────────────

interface ThreadHistoryControlProps {
  /** Fetches the channel's conversation list. Called on every open. */
  getConversations: () => Promise<ConversationSummary[]>;
  /** The thread the panel is currently attached to. */
  activeId: string;
  /**
   * Resume a past thread. The caller owns the channel lifecycle — for
   * Williams that means createSession before the SSE reconnect (§6).
   */
  onResume: (id: string) => void;
  /** Persona first name for the popover heading. */
  personaName: string;
}

export function ThreadHistoryControl({
  getConversations,
  activeId,
  onResume,
  personaName,
}: ThreadHistoryControlProps) {
  const { t } = useTranslation('chat');
  const [open, setOpen] = useState(false);
  // null = in flight; the fetch re-runs on every open so a thread retired a
  // moment ago is already offered back.
  const [conversations, setConversations] = useState<ConversationSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) return;
    setConversations(null);
    setLoadError(null);
    getConversations()
      .then(setConversations)
      .catch(() => {
        setLoadError(t('history.loadFailed'));
      });
  }, [getConversations, t]);

  const handleResume = useCallback((id: string) => {
    setOpen(false);
    if (id !== activeId) onResume(id);
  }, [activeId, onResume]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('history.trigger')}
          title={t('history.trigger')}
          // #2603 / #2644 (D-2420): 28 px drawn, 44 px reach on a coarse
          // pointer. It sits in the widget header's `isolate` button group, so
          // the reach goes BEHIND (-z-10): as a same-level `COARSE_HIT_ALONE` it took
          // the Expand button's edge (#2643 c).
          className={cn(
            'p-1.5 rounded hover:bg-stevens-indigo-solid-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current',
            COARSE_HIT_28,
          )}
        >
          <History className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      {/* The widget panel sits at z-[9990]; the portal-rendered popover must
          clear it or it opens underneath its own trigger. */}
      <PopoverContent align="end" sideOffset={8} className="z-[9995] w-80 p-0 overflow-hidden">
        <header className="px-4 pt-3 pb-2 border-b border-border/60">
          <MonoLabel className="block">
            {t('history.heading')}{conversations ? ` · ${formatNumber(conversations.length)}` : ''}
          </MonoLabel>
          <h3 className="font-serif text-base leading-tight mt-0.5">
            <Trans
              t={t}
              i18nKey="history.title"
              values={{ companion: personaName }}
              components={[<em key="name" className="not-italic text-stevens-indigo" />]}
            />
          </h3>
        </header>
        {loadError ? (
          <p className="px-4 py-5 text-body text-status-danger-fg">{loadError}</p>
        ) : conversations === null ? (
          <div className="flex items-center justify-center gap-2 px-4 py-6 text-stevens-ink-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-body">{t('history.loading')}</span>
          </div>
        ) : (
          <ThreadHistoryList
            conversations={conversations}
            activeId={activeId}
            onResume={handleResume}
            className="max-h-72 overflow-y-auto"
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
